export type WorkStatus = 'Current product work' | 'Pilot evidence' | 'Implementation documented';

export interface WorkItem {
  slug: string;
  eyebrow: string;
  title: string;
  shortTitle: string;
  summary: string;
  role: string;
  status: WorkStatus;
  year: string;
  accent: string;
  image: string;
  video?: string;
  screenTitle: string;
  screenSubtitle: string;
  screenItems: string[];
  problem: string;
  contribution: string[];
  decisions: { title: string; body: string }[];
  proof: string[];
  clientFit: string;
  serviceSlugs: string[];
}

export interface ServiceOffer {
  slug: string;
  number: string;
  title: string;
  buyerProblem: string;
  deliverable: string;
  boundaries: string;
  workSlug: string;
}

export const workItems: WorkItem[] = [
  {
    slug: 'ldpro-suite',
    eyebrow: 'Flagship system · current product work',
    title: 'LDPro Suite',
    shortTitle: 'LDPro Suite',
    summary: 'A connected family of learning products that links authoring, interactive practice, course delivery, and evidence.',
    role: 'Learning product design, instructional design, and hands-on development',
    status: 'Current product work',
    year: '2026',
    accent: '#a7f3d0',
    image: '/media/ldpro-suite-overview.png',
    video: '/media/ldpro-suite-motion.mp4',
    screenTitle: 'One learning journey, three focused tools',
    screenSubtitle: 'Create → Practise → Deliver',
    screenItems: ['Shared suite identity', 'Product-owned workflows', 'Release-aware learning evidence'],
    problem: 'Learning teams need focused tools for creating content, building practice, and delivering courses, but the experience should still feel coherent to the person moving through it.',
    contribution: [
      'Defined the boundaries between the suite shell, shared identity and asset services, authoring, interactive activities, and course delivery.',
      'Designed cross-product navigation and the workflow that moves an author from an idea to a learner-ready experience.',
      'Worked across product UI, backend routes, tenant boundaries, release behavior, and integration contracts.',
    ],
    decisions: [
      { title: 'Shared foundations', body: 'Identity, assets, and suite navigation are shared so product teams can stay focused on the learning job each tool performs.' },
      { title: 'Clear ownership', body: 'Authoring, activity design, and course delivery remain distinct. That keeps a course platform from becoming an editor and an editor from becoming an LMS.' },
      { title: 'Evidence with context', body: 'Published learning needs a stable release and a delivery context so later edits do not quietly rewrite historical learner work.' },
    ],
    proof: ['Multi-app suite architecture documented in the LDPro repository', 'Shared authentication and asset patterns across product boundaries', 'Cross-app workflows shaped around author and learner tasks'],
    clientFit: 'Branded learning platforms, internal academies, and teams deciding which training workflow deserves a purpose-built tool.',
    serviceSlugs: ['platform-discovery', 'custom-lms'],
  },
  {
    slug: 'eduease',
    eyebrow: 'Custom LMS workflow · product case study',
    title: 'EduEase',
    shortTitle: 'EduEase',
    summary: 'Course delivery designed around people, attempts, feedback, completion, and administration—not only a content player.',
    role: 'Learning workflow design and full-stack product implementation',
    status: 'Implementation documented',
    year: '2026',
    accent: '#93c5fd',
    image: '/media/eduease-dashboard.png',
    screenTitle: 'A course workflow an administrator can trust',
    screenSubtitle: 'Enroll → Learn → Submit → Review',
    screenItems: ['Role-aware course access', 'Attempt and submission boundaries', 'Progress that survives real use'],
    problem: 'A useful LMS has to keep learner activity, review workflows, and administrative truth aligned. Small inconsistencies become expensive when training is required or evidence matters.',
    contribution: [
      'Designed learner, reviewer, facilitator, and administrator journeys around explicit access and progress rules.',
      'Implemented and audited flows for submissions, artifacts, reviews, credentials, and recovery behavior.',
      'Used synthetic scenarios and focused tests to find race conditions and incomplete state transitions.',
    ],
    decisions: [
      { title: 'Make roles visible', body: 'The same artifact can be a learner submission, a reviewer assignment, or an administrative record. Each relationship needs its own authorization rule.' },
      { title: 'Treat progress as durable', body: 'A learner should not have to repair a lost attempt after a refresh, timeout, or interrupted background job.' },
      { title: 'Keep claims bounded', body: 'The case study distinguishes documented implementation from provider integrations and pilot evidence that still need independent validation.' },
    ],
    proof: ['Current LMS handoff records tested storage, reviewer, submission, and recovery paths', 'Express and React product implementation in the LDPro workspace', 'Release notes separate deployed corrections from remaining engineering work'],
    clientFit: 'Onboarding portals, customer academies, credential workflows, and reporting extensions where access and completion need careful rules.',
    serviceSlugs: ['custom-lms', 'platform-discovery'],
  },
  {
    slug: 'anythingscorm',
    eyebrow: 'Authoring tool · delivery case study',
    title: 'AnythingSCORM',
    shortTitle: 'AnythingSCORM',
    summary: 'An authoring workflow that carries an editable idea through review, publication, and a learner experience with continuity.',
    role: 'Authoring experience, delivery architecture, and runtime reliability',
    status: 'Pilot evidence',
    year: '2026',
    accent: '#fcd34d',
    image: '/media/anythingscorm-authoring.png',
    screenTitle: 'From source material to a learner-ready lesson',
    screenSubtitle: 'Draft → Review → Publish → Track',
    screenItems: ['Structured authoring', 'Preview before release', 'SCORM and hosted delivery paths'],
    problem: 'Authors lose time when source content, editing, preview, publishing, and tracking live in disconnected places. Delivery also has to preserve progress when a learner moves through a course.',
    contribution: [
      'Shaped an authoring experience that keeps the content model understandable while supporting interactive learning.',
      'Worked on publication and runtime behavior, including session continuity across lesson pages.',
      'Connected the authoring workflow to the suite pilot while keeping independent LMS compatibility as a separate claim to verify.',
    ],
    decisions: [
      { title: 'Separate source from release', body: 'An author can improve a draft without silently changing the experience attached to an existing learner assignment.' },
      { title: 'Design around the actual runtime', body: 'A course package must maintain a session across page changes; initialization and termination belong to the course lifecycle.' },
      { title: 'Name the destination', body: 'SCORM, hosted delivery, and a native suite handoff have different guarantees. The interface should make the selected destination clear.' },
    ],
    proof: ['AnythingSCORM completion evidence records a deployed Arcade UI and suite pilot', 'Runtime continuity fixes covered SCORM 1.2 and 2004 generated scripts', 'Independent LMS certification remains a separate future validation step'],
    clientFit: 'Custom authoring interfaces, branded content generators, reusable lesson systems, and LMS-connected publishing workflows.',
    serviceSlugs: ['authoring-tool', 'interactive-learning'],
  },
  {
    slug: 'gamify-ai',
    eyebrow: 'Interactive learning · product case study',
    title: 'Gamify AI',
    shortTitle: 'Gamify AI',
    summary: 'Purposeful interaction design that helps an author turn an objective into practice, feedback, and a playable experience.',
    role: 'Interaction design, guided authoring, AI-assisted workflow design, and implementation',
    status: 'Implementation documented',
    year: '2026',
    accent: '#f9a8d4',
    image: '/media/gamify-ai-activity.png',
    video: '/media/gamify-ai-practice.mp4',
    screenTitle: 'Practice should feel like the work',
    screenSubtitle: 'Objective → Activity → Feedback',
    screenItems: ['Decision-based practice', 'Human review of AI drafts', 'Learner and author views'],
    problem: 'Interactive learning is easy to make noisy. The useful version starts with what someone should be able to do and chooses mechanics that help them practise that task.',
    contribution: [
      'Mapped interaction types to objectives, feedback, facilitation, and learner evidence.',
      'Designed guided creation patterns that keep AI in an assistant role with review before acceptance.',
      'Worked across the interaction engine, examples, media lifecycle, release behavior, and delivery contracts.',
    ],
    decisions: [
      { title: 'Show the experience first', body: 'A playable example gives an author a better starting point than a catalog of implementation options.' },
      { title: 'Review before replacement', body: 'AI can propose distractors, explanations, or a scenario, but the author sees and accepts the change.' },
      { title: 'Protect the learner record', body: 'Published content and scored attempts need stable snapshots so an improvement does not rewrite yesterday’s evidence.' },
    ],
    proof: ['Interaction engine includes quizzes, matching, ordering, scenarios, video, and participation patterns', 'Dated audit records local reliability and integration slices', 'Production fault-injection, historical retention, and usability evidence remain explicit follow-up work'],
    clientFit: 'Scenario learning, interactive onboarding, practice tools, and reusable activity editors that support a real objective.',
    serviceSlugs: ['interactive-learning', 'authoring-tool'],
  },
];

export const serviceOffers: ServiceOffer[] = [
  {
    slug: 'platform-discovery',
    number: '01',
    title: 'Learning-platform discovery',
    buyerProblem: 'You are unsure whether to buy, configure, or build the workflow your learning team needs.',
    deliverable: 'A workflow map, prioritized requirements, one critical-flow prototype, architecture options, and a build estimate.',
    boundaries: 'A focused decision package. Production implementation, content migration, and unlimited research are separate scopes.',
    workSlug: 'ldpro-suite',
  },
  {
    slug: 'custom-lms',
    number: '02',
    title: 'Custom LMS workflows',
    buyerProblem: 'Your existing LMS cannot express the learner, reviewer, or administrator journey you actually run.',
    deliverable: 'One defined audience and workflow with agreed access, completion, reporting rules, documentation, and handover.',
    boundaries: 'Roles, integrations, data migration, content volume, hosting, and support are specified before the build begins.',
    workSlug: 'eduease',
  },
  {
    slug: 'authoring-tool',
    number: '03',
    title: 'Custom authoring tools',
    buyerProblem: 'Repeated course production is slow, inconsistent, or trapped in a workflow that does not fit your content.',
    deliverable: 'One content model and editor, a preview, one agreed publishing destination, validation, and documentation.',
    boundaries: 'AI providers, collaboration, translation, templates, and additional export standards are separately scoped.',
    workSlug: 'anythingscorm',
  },
  {
    slug: 'interactive-learning',
    number: '04',
    title: 'Interactive learning experiences',
    buyerProblem: 'Your training needs people to practise decisions and receive useful feedback, not just click through slides.',
    deliverable: 'One short activity or branching scenario with agreed mechanics, feedback, accessible alternatives, and delivery format.',
    boundaries: 'Content volume, assessment rules, LMS integration, and ongoing updates are explicit in the proposal.',
    workSlug: 'gamify-ai',
  },
];

export const capabilityGroups = [
  { title: 'Learning design', items: ['Instructional strategy', 'Scenario and simulation design', 'Curriculum architecture', 'Assessment and feedback'] },
  { title: 'Product development', items: ['React and TypeScript', 'Node.js workflows', 'LMS and authoring systems', 'SCORM, xAPI, and release behavior'] },
  { title: 'Delivery judgment', items: ['Stakeholder partnership', 'Workflow and process design', 'Accessibility-minded QA', 'Documentation and handover'] },
];
